import React, { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import CryptoJS from 'crypto-js';

const Base_Url = process.env.REACT_APP_API_BASE_URL;
// SECURITY WARNING: AES key should be moved to backend for production
// Frontend encryption keys are visible to users and not secure
const AES_KEY = process.env.REACT_APP_ENCRYPTION_AES_KEY;

const ResetPassword = () => {
    const { search } = useLocation();
    const navigate = useNavigate();
    const [token, setToken] = useState('');
    const [isTokenValid, setIsTokenValid] = useState(false);
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPasswordRules, setShowPasswordRules] = useState(false);
    const [passwordrequired, setPasswordrequired] = useState('');
    const passwordInputRef = useRef(null);
    const rulesRef = useRef(null);

    const encryptData = (data, key = AES_KEY) => {
        const dataStr = typeof data === 'object' ? JSON.stringify(data) : data;
        return CryptoJS.AES.encrypt(dataStr, key).toString();
    };

    useEffect(() => {
        const queryParams = new URLSearchParams(search);
        const tokenFromUrl = queryParams.get('token');

        if (!tokenFromUrl) {
            toast.error("Missing token. Redirecting...");
            return navigate('/');
        }

        const validateToken = async () => {
            try {
                const res = await axios.get(`${Base_Url}/users/validate-reset-token?token=${tokenFromUrl}`);
                if (res.data.status === 'success') {
                    setToken(tokenFromUrl);
                    setIsTokenValid(true);
                } else {
                    throw new Error();
                }
            } catch (error) {
                const errorMessage = error?.response?.data?.error?.errors || "Invalid or expired token";
                toast.error(errorMessage);
                setTimeout(() => {
                    navigate('/');
                }, 3000);
            }
        };

        validateToken();
    }, [search, navigate]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                rulesRef.current &&
                !rulesRef.current.contains(event.target) &&
                passwordInputRef.current &&
                !passwordInputRef.current.contains(event.target)
            ) {
                setShowPasswordRules(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const handlePasswordSubmit = async (e) => {
        e.preventDefault();

        if (!password || !confirmPassword) {
            toast.warning("Please fill all password fields.");
            return;
        }

        if (password !== confirmPassword) {
            toast.error("Passwords do not match.");
            return;
        }

        if (!isPasswordValid(password)) {
            setPasswordrequired("Password does not meet required conditions.");
            return;
        }

        try {
            const encryptedPayload = encryptData({ token, newPassword: password });
            const response = await axios.post(`${Base_Url}/users/reset-password`, {
                payload: encryptedPayload
            });

            if (response.data?.status === 'success') {
                toast.success(response.data.message || "Password reset successfully!");
                setTimeout(() => {
                    navigate('/');
                }, 3000);
            } else {
                toast.error(response.data?.message || "Failed to reset password");
            }
        } catch (error) {
            if (process.env.NODE_ENV === 'development') {
                console.error("Error resetting password:", error);
            }
            const backendMsg =
                error.response?.data?.error?.message ||
                error.response?.data?.message ||
                "Something went wrong. Please try again.";
            toast.error(backendMsg);
        }
    };

    const isPasswordValid = (pass) => {
        const length = pass.length >= 8;
        const uppercase = /[A-Z]/.test(pass);
        const lowercase = /[a-z]/.test(pass);
        const number = /[0-9]/.test(pass);
        const specialChar = /[!@#$%^&*(),.?":{}|<>]/.test(pass);
        return length && uppercase && lowercase && number && specialChar;
    };

    const passwordRules = [
        { label: "At least 8 characters", test: (p) => p.length >= 8 },
        { label: "At least one uppercase letter", test: (p) => /[A-Z]/.test(p) },
        { label: "At least one lowercase letter", test: (p) => /[a-z]/.test(p) },
        { label: "At least one number", test: (p) => /[0-9]/.test(p) },
        { label: "At least one special character", test: (p) => /[!@#$%^&*(),.?":{}|<>]/.test(p) },

    ];

    return (
        <div className='hold-transition login-page accent-custom'>
            <ToastContainer autoClose={3000} />
            <div className="login-box login_div">
                <div className="card card-outline card-primary">
                    <div className="card-header text-center">
                        <Link to="/" className="login_logo">
                            <img src="/assets/img/logo.png" alt='logo' border={0} />
                        </Link>
                    </div>
                    <div className="card-body d-flex flex-row flex_flow_rowwrap">
                        <div className="col-12 col-sm-12 col-md-12">
                            <h5 className="login-box-msg mb-2">Create New Password</h5>

                            {isTokenValid ? (
                                <form onSubmit={handlePasswordSubmit} autoComplete="off">
                                    <div className="input-group mb-2">
                                        <input
                                            ref={passwordInputRef}
                                            type="password"
                                            className="form-control"
                                            placeholder="New Password"
                                            autoComplete="New Password"
                                            value={password}
                                            onFocus={() => {
                                                setShowPasswordRules(true);
                                                setPasswordrequired('');
                                            }}
                                            onChange={(e) => setPassword(e.target.value)}
                                        />
                                        <div className="input-group-append">
                                            <div className="input-group-text">
                                                <i className="fas fa-lock" />
                                            </div>
                                        </div>
                                    </div>

                                    <div
                                        ref={rulesRef}
                                        className={`password-rules-wrapper ${showPasswordRules ? 'show' : ''}`}
                                    >
                                        <ul className="mb-2" style={{ listStyle: 'none', paddingLeft: 0 }}>
                                            {passwordRules.map((rule, index) => (
                                                <li
                                                    key={index}
                                                    style={{
                                                        color: rule.test(password) ? 'green' : 'gray',
                                                        display: 'flex',
                                                        alignItems: 'center'
                                                    }}
                                                >
                                                    <i
                                                        className={`fa ${rule.test(password) ? 'fa-check-circle' : 'fa-circle'}`}
                                                        style={{ marginRight: '6px' }}
                                                    ></i>
                                                    {rule.label}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>

                                    {passwordrequired && (
                                        <div className="text-danger mb-2" style={{ fontSize: '0.9em' }}>{passwordrequired}</div>
                                    )}

                                    <div className="input-group mb-2">
                                        <input
                                            type="password"
                                            className="form-control"
                                            placeholder="Confirm New Password"
                                            value={confirmPassword}
                                            onChange={(e) => setConfirmPassword(e.target.value)}
                                        />
                                        <div className="input-group-append">
                                            <div className="input-group-text">
                                                <i className="fas fa-lock" />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="row">
                                        <div className="col-12 mt-3">
                                            <button type="submit" className="btn btn-blue btn-block">Reset Password</button>
                                        </div>
                                    </div>
                                </form>
                            ) : (
                                <div className="text-center mt-3">
                                    <p>Validating your token...</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ResetPassword;
